import { db, type OutboxFieldNote, type OutboxPunch } from "@/lib/db";
import { clientLog } from "@/lib/client-logger";
import {
  isDexieStorageError,
  isStorageDegraded,
  markStorageDegraded,
  StorageUnavailableError,
} from "@/lib/storageCapability";

const SYNCED_RETENTION_MS = 3 * 24 * 60 * 60 * 1000; // Reduced to 3 days. Background/auto prunes use retention; the header "Clear synced" button (clearAllSyncedData) clears *all* synced immediately + the rq query cache.

function assertStorageAvailable(op: string): void {
  if (isStorageDegraded()) {
    throw new StorageUnavailableError(
      `Offline storage is unavailable (${op}). Use online submit or switch to normal Safari.`,
    );
  }
}

function handleEnqueueStorageFailure(err: unknown, context: string): never {
  const msg = err instanceof Error ? err.message : String(err);
  if (isDexieStorageError(err) || /null is not an object/i.test(msg)) {
    markStorageDegraded(`${context}: ${msg}`);
  }
  throw err instanceof Error ? err : new Error(msg);
}

export type CapturedPunch = {
  action: "clock_in" | "clock_out";
  fields: Record<string, string>;
  selfieBlob: Blob;
  selfieName: string;
  turnstileToken: string | null;
  userId: number;
};

/** Statuses that still need to reach the server ("syncing" covers crash recovery). */
const UNSYNCED_STATUSES = ["pending", "syncing", "failed", "auth_required"] as const;

async function requestPersistentStorage(): Promise<void> {
  try {
    const already = await db.kv.get("storagePersisted");
    if (already?.value === true) return;
    if (navigator.storage?.persist) {
      const granted = await navigator.storage.persist();
      await db.kv.put({ key: "storagePersisted", value: granted });
    }
  } catch {
    // Best effort — never block a punch on storage API quirks.
  }
}

/**
 * WebKit/iOS often fails IndexedDB writes with
 * "Error preparing Blob/File data to be stored in object store" when the Blob
 * is still backed by a live canvas/camera handle. Re-wrapping from an
 * ArrayBuffer makes the bytes durable and IDB-safe.
 */
async function materializeSelfieBlob(blob: Blob): Promise<Blob> {
  try {
    const buffer = await blob.arrayBuffer();
    return new Blob([buffer], { type: blob.type || "image/jpeg" });
  } catch {
    return blob;
  }
}

export async function enqueuePunch(captured: CapturedPunch): Promise<OutboxPunch> {
  assertStorageAvailable("enqueuePunch");
  const selfieBlob = await materializeSelfieBlob(captured.selfieBlob);
  const punch: OutboxPunch = {
    uuid: crypto.randomUUID(),
    userId: captured.userId,
    action: captured.action,
    fields: captured.fields,
    selfieBlob,
    selfieName: captured.selfieName,
    turnstileToken: captured.turnstileToken,
    tokenIssuedAt: captured.turnstileToken ? Date.now() : null,
    status: "pending",
    attempts: 0,
    lastError: null,
    lastErrorStatus: null,
    nextAttemptAt: 0,
    createdAt: Date.now(),
    syncedAt: null,
    serverEntryId: null,
  };
  try {
    const id = await db.outbox.add(punch);
    clientLog("info", "outbox:punch:enqueue_success", {
      id,
      uuid: punch.uuid.slice(0, 8),
      action: punch.action,
      userId: punch.userId,
      hasSelfie: !!punch.selfieBlob,
      selfieBytes: punch.selfieBlob?.size ?? 0,
      fieldCount: Object.keys(punch.fields).length,
    });
    void requestPersistentStorage();
    return { ...punch, id };
  } catch (err: any) {
    // Common transient IDB error on some browsers/devices when writing large blobs
    // or under memory/storage pressure. Try to reopen the DB and retry once.
    const msg = String(err?.message || err?.name || "").toLowerCase();
    const transient =
      msg.includes("connection") ||
      msg.includes("lost") ||
      msg.includes("unknownerror") ||
      err?.name === "UnknownError";
    if (transient && !isStorageDegraded()) {
      try {
        await db.open();
        const id = await db.outbox.add(punch);
        clientLog("warn", "outbox:punch:enqueue_retry_success", {
          id,
          uuid: punch.uuid.slice(0, 8),
          action: punch.action,
          errorName: err?.name,
          errorMessage: String(err?.message || err?.name || ""),
        });
        void requestPersistentStorage();
        return { ...punch, id };
      } catch (retryErr) {
        clientLog("error", "outbox:punch:enqueue_retry_failed", {
          uuid: punch.uuid.slice(0, 8),
          action: punch.action,
          errorName: (retryErr as any)?.name,
          errorMessage: String((retryErr as any)?.message || (retryErr as any)?.name || ""),
        });
        handleEnqueueStorageFailure(retryErr, "enqueuePunch_retry");
      }
    }
    clientLog("error", "outbox:punch:enqueue_failed", {
      uuid: punch.uuid.slice(0, 8),
      action: punch.action,
      errorName: err?.name,
      errorMessage: String(err?.message || err?.name || ""),
    });
    handleEnqueueStorageFailure(err, "enqueuePunch");
  }
}

async function unsyncedPunches(userId: number): Promise<OutboxPunch[]> {
  const rows = await db.outbox.where("userId").equals(userId).sortBy("id");
  return rows.filter((row) => row.status !== "synced");
}

export async function syncablePunches(userId: number): Promise<OutboxPunch[]> {
  const rows = await db.outbox.where("userId").equals(userId).sortBy("id");
  return rows.filter((row) => row.status === "pending" || row.status === "syncing");
}

async function unsyncedCountAllUsers(): Promise<number> {
  return db.outbox.filter((row) => row.status !== "synced").count();
}

export async function markSyncing(id: number): Promise<void> {
  await db.outbox.update(id, { status: "syncing" });
}

export async function markSynced(id: number, serverEntryId: number): Promise<void> {
  await db.outbox.update(id, {
    status: "synced",
    syncedAt: Date.now(),
    serverEntryId,
    selfieBlob: null,
    lastError: null,
    lastErrorStatus: null,
  });
}

export async function markRetryLater(id: number, attempts: number, nextAttemptAt: number, status_: number | null, message: string): Promise<void> {
  await db.outbox.update(id, {
    status: "pending",
    attempts,
    nextAttemptAt,
    lastError: message,
    lastErrorStatus: status_,
  });
}

export async function markFailed(id: number, status_: number | null, message: string): Promise<void> {
  await db.outbox.update(id, {
    status: "failed",
    lastError: message,
    lastErrorStatus: status_,
  });
}

export async function markAuthRequired(userId: number): Promise<void> {
  await db.outbox
    .where("userId")
    .equals(userId)
    .modify((row) => {
      if (row.status === "pending" || row.status === "syncing") {
        row.status = "auth_required";
      }
    });
}

export async function markAllAuthRequired(): Promise<void> {
  await db.outbox.toCollection().modify((row: OutboxPunch) => {
    if (row.status === "pending" || row.status === "syncing") {
      row.status = "auth_required";
    }
  });
}

/** After a successful login, put the user's parked punches back in the queue. */
export async function resumeAfterLogin(userId: number): Promise<void> {
  await db.outbox
    .where("userId")
    .equals(userId)
    .modify((row) => {
      if (row.status === "auth_required") {
        row.status = "pending";
        row.nextAttemptAt = 0;
      }
    });
}

export async function retryPunch(id: number): Promise<void> {
  await db.outbox.update(id, { status: "pending", nextAttemptAt: 0, attempts: 0 });
}

/** Explicit user action only — a discarded punch is gone for good.
 * Note: per UI policy, this should generally only be used for cleanup of synced records,
 * not for unsynced/pending items (to avoid losing work). Use retry instead for failed items.
 */
async function discardPunch(id: number): Promise<void> {
  await db.outbox.delete(id);
}

export async function pruneSynced(olderThanMs: number = SYNCED_RETENTION_MS): Promise<number> {
  const cutoff = Date.now() - olderThanMs;
  return db.outbox.filter((row) => row.status === "synced" && (row.syncedAt ?? 0) < cutoff).delete();
}

// =============================================================================
// Field Note offline outbox (parallel to punches; reuses status machine, prune
// pattern, persist request, auth_required, etc. for minimal new code).
// =============================================================================

export type CapturedFieldNote = {
  userId: number;
  // The exact payload from the "New Field Note" form (including client-captured submitted_at).
  // Values are string | null | undefined to match form state; serialized on enqueue.
  payload: Record<string, string | null | undefined>;
};

export async function enqueueFieldNote(captured: CapturedFieldNote): Promise<OutboxFieldNote> {
  assertStorageAvailable("enqueueFieldNote");
  const note: OutboxFieldNote = {
    uuid: crypto.randomUUID(),
    userId: captured.userId,
    fields: { ...captured.payload },
    status: "pending",
    attempts: 0,
    lastError: null,
    lastErrorStatus: null,
    nextAttemptAt: 0,
    createdAt: Date.now(),
    syncedAt: null,
    serverNoteId: null,
  };
  try {
    const id = await db.fieldNotesOutbox.add(note);
    clientLog("info", "outbox:field_note:enqueue_success", {
      id,
      uuid: note.uuid.slice(0, 8),
      userId: note.userId,
      fieldCount: Object.keys(note.fields).length,
    });
    void requestPersistentStorage();
    return { ...note, id };
  } catch (err: any) {
    const msg = String(err?.message || err?.name || "").toLowerCase();
    const transient =
      msg.includes("connection") ||
      msg.includes("lost") ||
      msg.includes("unknownerror") ||
      err?.name === "UnknownError";
    if (transient && !isStorageDegraded()) {
      try {
        await db.open();
        const id = await db.fieldNotesOutbox.add(note);
        clientLog("warn", "outbox:field_note:enqueue_retry_success", {
          id,
          uuid: note.uuid.slice(0, 8),
          errorName: err?.name,
          errorMessage: String(err?.message || err?.name || ""),
        });
        void requestPersistentStorage();
        return { ...note, id };
      } catch (retryErr) {
        clientLog("error", "outbox:field_note:enqueue_retry_failed", {
          uuid: note.uuid.slice(0, 8),
          errorName: (retryErr as any)?.name,
          errorMessage: String((retryErr as any)?.message || (retryErr as any)?.name || ""),
        });
        handleEnqueueStorageFailure(retryErr, "enqueueFieldNote_retry");
      }
    }
    clientLog("error", "outbox:field_note:enqueue_failed", {
      uuid: note.uuid.slice(0, 8),
      errorName: err?.name,
      errorMessage: String(err?.message || err?.name || ""),
    });
    handleEnqueueStorageFailure(err, "enqueueFieldNote");
  }
}

export async function syncableFieldNotes(userId: number): Promise<OutboxFieldNote[]> {
  const rows = await db.fieldNotesOutbox.where("userId").equals(userId).sortBy("id");
  return rows.filter((row) => row.status === "pending" || row.status === "syncing");
}

export async function markFieldNoteSyncing(id: number): Promise<void> {
  await db.fieldNotesOutbox.update(id, { status: "syncing" });
}

export async function markFieldNoteSynced(id: number, serverNoteId: number): Promise<void> {
  await db.fieldNotesOutbox.update(id, {
    status: "synced",
    syncedAt: Date.now(),
    serverNoteId,
    lastError: null,
    lastErrorStatus: null,
  });
}

export async function markFieldNoteRetryLater(
  id: number,
  attempts: number,
  nextAttemptAt: number,
  status_: number | null,
  message: string,
): Promise<void> {
  await db.fieldNotesOutbox.update(id, {
    status: "pending",
    attempts,
    nextAttemptAt,
    lastError: message,
    lastErrorStatus: status_,
  });
}

export async function markFieldNoteFailed(id: number, status_: number | null, message: string): Promise<void> {
  await db.fieldNotesOutbox.update(id, {
    status: "failed",
    lastError: message,
    lastErrorStatus: status_,
  });
}

export async function markFieldNotesAuthRequired(userId: number): Promise<void> {
  await db.fieldNotesOutbox
    .where("userId")
    .equals(userId)
    .modify((row) => {
      if (row.status === "pending" || row.status === "syncing") {
        row.status = "auth_required";
      }
    });
}

export async function markAllFieldNotesAuthRequired(): Promise<void> {
  await db.fieldNotesOutbox.toCollection().modify((row: OutboxFieldNote) => {
    if (row.status === "pending" || row.status === "syncing") {
      row.status = "auth_required";
    }
  });
}

export async function resumeFieldNotesAfterLogin(userId: number): Promise<void> {
  await db.fieldNotesOutbox
    .where("userId")
    .equals(userId)
    .modify((row) => {
      if (row.status === "auth_required") {
        row.status = "pending";
        row.nextAttemptAt = 0;
      }
    });
}

export async function retryFieldNote(id: number): Promise<void> {
  await db.fieldNotesOutbox.update(id, { status: "pending", nextAttemptAt: 0, attempts: 0 });
}

async function discardFieldNote(id: number): Promise<void> {
  await db.fieldNotesOutbox.delete(id);
  // Note: UI policy prefers discard only for synced contents (use retry for failed/unsynced).
}

export async function pruneSyncedFieldNotes(olderThanMs: number = SYNCED_RETENTION_MS): Promise<number> {
  const cutoff = Date.now() - olderThanMs;
  return db.fieldNotesOutbox
    .filter((row) => row.status === "synced" && (row.syncedAt ?? 0) < cutoff)
    .delete();
}

/** Clear *all* synced records immediately (no retention cutoff). Used by the explicit user "clear" action.
 * Only removes already-synced items; pending/unsynced/failed items are left untouched.
 */
async function clearAllSynced(): Promise<number> {
  return db.outbox.filter((row) => row.status === "synced").delete();
}

async function clearAllSyncedFieldNotes(): Promise<number> {
  return db.fieldNotesOutbox.filter((row) => row.status === "synced").delete();
}

/** Clear all synced outbox rows (punches + field notes) plus the react-query
 * persisted cache (rq-cache in kv). Used by the explicit user storage-clear action
 * to reclaim as much local space as possible after successful syncs.
 */
export async function clearAllSyncedData(): Promise<void> {
  await Promise.all([
    clearAllSynced(),
    clearAllSyncedFieldNotes(),
  ]);
  try {
    await db.kv.delete("rq-cache");
  } catch {
    // best effort
  }
}
